import type { FormEvent, KeyboardEvent, ReactNode } from "react"
import { useRef } from "react"
import { useMutation } from "@tanstack/react-query"
import { CheckCircle2, Inbox, Loader2 } from "lucide-react"

import { ApiFailure, api, monthName } from "./api"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

// Building blocks and formatters shared by every page.

export type MoneyFigure = {
  revenue: string
  personnel?: string
  other?: string
  totalCost: string
  profit: string
  margin: number | null
}

export type Dashboard = {
  currentMonth: { year: number; month: number }
  allocation: { id: number; status: string } | null
  divisionPending: number
  buPending: number
  unreadNotifications: number
}

export const roleLabel: Record<string, string> = {
  admin: "مدیر سامانه",
  cfo: "مدیر مالی",
  employee: "کارمند",
  division_head: "مدیر واحد",
  bu_head: "مدیر کسب‌وکار",
}

/** Every screen: title + one-line purpose on the start side, the page's own controls on the end side. */
export function Page({ title, description, actions, children }: { title: string; description?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="mx-auto grid w-full max-w-7xl gap-6">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="min-w-0 max-w-3xl">
          <h1 className="text-2xl font-semibold tracking-tight text-balance">{title}</h1>
          {description && <p className="mt-1.5 text-sm leading-6 text-pretty text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      {children}
    </div>
  )
}

export function Metric({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>{title}</CardDescription>
        <CardTitle>{value}</CardTitle>
      </CardHeader>
    </Card>
  )
}

export function DataCard({ headers, empty, children }: { headers: string[]; empty: string; children: ReactNode }) {
  const hasRows = Array.isArray(children) ? children.length > 0 : Boolean(children)

  // No outer scroller here: Table already renders its own top-scrollbar container;
  // a second .scroll-top wrapper would flip the scrollbar back out of view.
  return (
    <Card>
      <CardContent className="pt-6">
        <Table className="min-w-full text-right">
          <TableHeader>
            <TableRow>{headers.map((header) => <TableHead className="whitespace-nowrap text-right" key={header}>{header}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {hasRows ? children : <TableRow><TableCell colSpan={headers.length}><EmptyState text={empty} /></TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

/** Lightweight confirm: same words the old window.confirm used, inside a trapped,
 * RTL-aware dialog instead of a native popup. No preview mechanics, no typed
 * acknowledgement — for high-stakes commits that need more, build a dedicated dialog. */
export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, cancelLabel = "انصراف", danger, pending, children, onConfirm }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  confirmLabel: string
  cancelLabel?: string
  danger?: boolean
  pending?: boolean
  children?: ReactNode
  onConfirm: () => void
}) {
  const cancelRef = useRef<HTMLButtonElement>(null)
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!pending) onOpenChange(next) }}>
      <DialogContent role="alertdialog" onOpenAutoFocus={(event) => { event.preventDefault(); cancelRef.current?.focus() }}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="whitespace-pre-line">{description}</DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter className="mt-4">
          <Button type="button" variant={danger ? "destructive" : "default"} disabled={pending} onClick={onConfirm}>
            {pending && <Loader2 className="size-4 animate-spin" />}{confirmLabel}
          </Button>
          <Button ref={cancelRef} type="button" variant="ghost" disabled={pending} onClick={() => onOpenChange(false)}>{cancelLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Arrow/Home/End support for div-based option groups (radiogroups, tablists) in
 * this RTL app: ArrowLeft/Down = next, ArrowRight/Up = previous. Attach as the
 * container's onKeyDown. With `follow` (default, for tabs) the newly focused option
 * is also selected; with `follow: false` (radios that switch data, e.g. years)
 * arrows only move focus and Enter/Space selects, so browsing never fires a change. */
export function listKeys(event: KeyboardEvent<HTMLElement>, optionRole: "radio" | "tab", onSelect: (index: number) => void, follow = true) {
  if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return
  const group = event.currentTarget
  const options = [...group.querySelectorAll<HTMLElement>(`[role="${optionRole}"]`)]
  if (!options.length) return
  const selectedAttr = optionRole === "radio" ? "aria-checked" : "aria-selected"
  const focused = options.indexOf(document.activeElement as HTMLElement)
  const current = focused === -1 ? options.findIndex((el) => el.getAttribute(selectedAttr) === "true") : focused
  const last = options.length - 1
  let next: number
  if (event.key === "Home") next = 0
  else if (event.key === "End") next = last
  else {
    const forward = event.key === "ArrowDown" || event.key === "ArrowLeft"
    next = (current === -1 ? (forward ? last : 0) : current) + (forward ? 1 : -1)
    next = (next + options.length) % options.length
  }
  event.preventDefault()
  if (follow) onSelect(next)
  options[next]?.focus()
}

export function LoadingCard() {
  return (
    <Card aria-busy="true">
      <CardContent className="grid gap-3" role="status">
        <span className="sr-only">در حال بارگذاری</span>
        <div className="h-4 w-40 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
        <div className="h-3 w-full max-w-md animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
        <div className="h-3 w-full max-w-xs animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
      </CardContent>
    </Card>
  )
}

/** `done` = nothing is waiting (a good outcome); otherwise there is simply no data yet. */
export function EmptyState({ text, done, children }: { text: string; done?: boolean; children?: ReactNode }) {
  const Icon = done ? CheckCircle2 : Inbox
  return (
    <div className="grid justify-items-center gap-2 py-10 text-center text-sm text-muted-foreground">
      <span className={done ? "grid size-9 place-items-center rounded-full bg-success/10 text-success" : "grid size-9 place-items-center rounded-full bg-muted"}><Icon className="size-4" aria-hidden /></span>
      <span className="max-w-sm text-pretty">{text}</span>
      {children}
    </div>
  )
}

export function formatMonthYear(year: number, month: number) {
  return `${monthName(month)} ${year.toLocaleString("fa-IR", { useGrouping: false })}`
}

/** Rial → million rial, the one money unit shown everywhere in the system. */
export function moneyInMillions(value: string) {
  const amount = Number(value) / 1_000_000
  return amount.toLocaleString("fa-IR", { maximumFractionDigits: 0 })
}

export function fiscalYearFor(year: number, month: number) {
  return month >= 7 ? year : year - 1
}

export function fiscalYearLabel(year: number) {
  const start = year.toLocaleString("fa-IR", { useGrouping: false })
  const end = (year + 1).toLocaleString("fa-IR", { useGrouping: false })
  return `سال مالی ${start}-${end}`
}

export function fiscalYearOptions(currentYear: number) {
  return Array.from({ length: 12 }, (_, index) => currentYear - 3 + index)
}

export function messageFor(error: unknown) {
  return error instanceof ApiFailure ? error.message : "عملیات انجام نشد. دوباره تلاش کنید."
}

export function submit(event: FormEvent, action: () => void) {
  event.preventDefault()
  action()
}

/**
 * Square-crops and shrinks a photo on the device before upload, so a phone photo of any size
 * fits the server's 2 MB limit (and the stored image stays small). White fills transparency.
 */
async function squarePhoto(file: File, size = 384): Promise<Blob> {
  const url = URL.createObjectURL(file)
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image()
      element.onload = () => resolve(element)
      element.onerror = () => reject(new Error("decode"))
      element.src = url
    })
    const side = Math.min(image.naturalWidth, image.naturalHeight)
    const out = Math.min(size, side)
    const canvas = document.createElement("canvas")
    canvas.width = out
    canvas.height = out
    const context = canvas.getContext("2d")!
    context.fillStyle = "#ffffff"
    context.fillRect(0, 0, out, out)
    context.imageSmoothingQuality = "high"
    context.drawImage(image, (image.naturalWidth - side) / 2, (image.naturalHeight - side) / 2, side, side, 0, 0, out, out)
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("encode"))), "image/jpeg", 0.88))
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Picks, shrinks and uploads a profile photo; any failure is said out loud, never swallowed. */
export function ProfilePictureUpload({ endpoint, label, onUploaded, variant = "outline" }: { endpoint: string; label: string; onUploaded?: (profileImageUrl: string) => void; variant?: "outline" | "default" }) {
  const input = useRef<HTMLInputElement>(null)
  const upload = useMutation({
    mutationFn: async (file: File) => {
      let photo: Blob
      try { photo = await squarePhoto(file) } catch { throw new ApiFailure("این تصویر باز نشد. یک عکس JPG یا PNG انتخاب کنید.", 0) }
      const body = new FormData()
      body.append("file", photo, "avatar.jpg")
      return api<{ profileImageUrl: string }>(endpoint, { method: "POST", body })
    },
    onSuccess: (data) => onUploaded?.(data.profileImageUrl),
  })

  return (
    <div className="grid justify-items-start gap-1.5">
      <Button type="button" variant={variant} size="sm" disabled={upload.isPending} onClick={() => { upload.reset(); input.current?.click() }}>
        {upload.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
        {upload.isPending ? "در حال بارگذاری…" : label}
      </Button>
      <input
        ref={input}
        className="sr-only"
        type="file"
        tabIndex={-1}
        aria-hidden
        accept="image/*"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) upload.mutate(file)
          event.currentTarget.value = ""
        }}
      />
      {upload.error && <p role="alert" className="text-sm text-loss">{messageFor(upload.error)}</p>}
      {upload.isSuccess && <p role="status" className="flex items-center gap-1 text-sm text-success"><CheckCircle2 className="size-4" aria-hidden /> عکس به‌روز شد.</p>}
    </div>
  )
}
